/**
 * パスワード入力（自動生成 / 自分で決める）。
 *
 * メールボックスのパスワードは契約者が **メールソフトに手で入力する** 値なので、
 * 生成した場合は必ずコピーできる形で見せる。強度メーターは目安であり、
 * 実際の受け入れ可否はサーバー側（`@mail-sv/shared` の検証）が決める。
 */
import * as React from 'react';
import { Button, Input, cn } from '@mail-sv/ui';
import { Eye, EyeOff, RefreshCw, Copy, Check } from 'lucide-react';

/** 紛らわしい文字（0/O, 1/l/I）を除いた英数字 + 記号。手入力の事故を減らす。 */
const ALPHABET = 'abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789!#%+-=?@';

export function generatePassword(length = 16): string {
  const bytes = new Uint32Array(length);
  crypto.getRandomValues(bytes);
  let out = '';
  for (let i = 0; i < length; i += 1) {
    // 剰余の偏りは無視できる範囲（32bit を 64 文字に割る）。
    out += ALPHABET[(bytes[i] ?? 0) % ALPHABET.length];
  }
  return out;
}

/** 0..4 の目安スコア。長さを最重視する（文字種より長さの方が効く）。 */
export function passwordScore(pw: string): number {
  if (pw.length === 0) return 0;
  let score = 0;
  if (pw.length >= 8) score += 1;
  if (pw.length >= 12) score += 1;
  if (pw.length >= 16) score += 1;
  const kinds =
    Number(/[a-z]/.test(pw)) + Number(/[A-Z]/.test(pw)) + Number(/\d/.test(pw)) + Number(/[^\w]/.test(pw));
  if (kinds >= 3) score += 1;
  return Math.min(4, score);
}

const SCORE_LABEL = ['入力してください', '弱い', 'やや弱い', '十分', '強い'] as const;
const SCORE_CLASS = [
  'bg-muted',
  'bg-destructive',
  'bg-warning',
  'bg-success',
  'bg-success',
] as const;

export function PasswordStrength({ value }: { value: string }) {
  const score = passwordScore(value);
  return (
    <div className="space-y-1">
      <div className="flex gap-1">
        {[1, 2, 3, 4].map((i) => (
          <span
            key={i}
            className={cn('h-1 flex-1 rounded-full', i <= score ? SCORE_CLASS[score] : 'bg-muted')}
          />
        ))}
      </div>
      <p className="text-xs text-muted-foreground">
        強度: {SCORE_LABEL[score]}（8 文字以上、英大文字・小文字・数字を混ぜてください）
      </p>
    </div>
  );
}

export function PasswordField({
  value,
  onChange,
  id,
  autoComplete = 'new-password',
  showGenerate = true,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  id?: string;
  autoComplete?: string;
  showGenerate?: boolean;
  placeholder?: string;
}) {
  const [visible, setVisible] = React.useState(false);
  const [copied, setCopied] = React.useState(false);

  const copy = React.useCallback(() => {
    void navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    });
  }, [value]);

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <div className="relative min-w-0 flex-1">
          <Input
            id={id}
            type={visible ? 'text' : 'password'}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            autoComplete={autoComplete}
            placeholder={placeholder}
            className="pr-10 font-mono"
          />
          <button
            type="button"
            onClick={() => setVisible((v) => !v)}
            aria-label={visible ? 'パスワードを隠す' : 'パスワードを表示する'}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:bg-hover hover:text-foreground"
          >
            {visible ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
          </button>
        </div>
        {showGenerate ? (
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              const pw = generatePassword();
              onChange(pw);
              setVisible(true);
            }}
          >
            <RefreshCw aria-hidden />
            自動生成
          </Button>
        ) : null}
        {value ? (
          <Button type="button" variant="outline" size="icon" onClick={copy} aria-label="パスワードをコピー">
            {copied ? <Check className="text-success" aria-hidden /> : <Copy aria-hidden />}
          </Button>
        ) : null}
      </div>
      <PasswordStrength value={value} />
    </div>
  );
}
